import { useCallback } from 'react';
import { useRequest } from '../useRequest';
// api
import api from 'utils/api';

/**
 * Onboarding/payout artist profiles of the logged-in user (`GET /artist-profile`).
 * One profile per invited identity; a user may hold several.
 *
 * NOT related to Collab Club's cc_artists profiles (`useUpdateArtistProfile`,
 * `Artist.isOnboarded`) — those cover the Collab Club directory only.
 */

export interface IBankingProof {
  fileName: string | null;
  uploadedAt: string | null;
}

export interface IArtistProfile {
  id: number;
  spotifyId: string | null;
  youtubeId: string | null;
  invitationId: number | null;
  onboardingRequired: boolean;
  /** First Spotify profile image, seeded at invitation time (read-only). */
  artistImage: string | null;
  fullName: string | null;
  artistName: string | null;
  dateOfBirth: string | null;
  nationality: string | null;
  phone: string | null;
  secondaryPhone: string | null;
  secondaryEmail: string | null;
  location: string | null;
  residentialAddress: string | null;
  residentialAddress2: string | null;
  city: string | null;
  /** Subdivision code (e.g. 'CA', 'NSW'); only for US/AU/CA addresses. */
  state: string | null;
  postalCode: string | null;
  country: string | null;
  /** ISO 3166-1 alpha-2, derived server-side from `country` (read-only). */
  countryCode: string | null;
  /** 'Yes' | 'No' — consent to receive the welcome gift. */
  welcomeGift: string | null;
  /** 'Residential' | 'Other' — set by the server while opted in. */
  welcomeGiftAddressType: string | null;
  giftAddress: string | null;
  giftAddress2: string | null;
  giftCity: string | null;
  giftState: string | null;
  giftPostalCode: string | null;
  giftCountry: string | null;
  giftCountryCode: string | null;
  /** CPF/CNPJ, kept only while the gift ships to Brazil. */
  taxNumber: string | null;
  spotifyUrl: string | null;
  instagram: string | null;
  tiktok: string | null;
  curveDownloaded: string | null;
  recoupmentEmail: string | null;
  goals: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  genre: string | null;
  techPlatformSignup: string | null;
  collabInterest: string | null;
  marketingCall: string | null;
  marketingActivations: string | null;
  bankAccountName: string | null;
  bankAccountAddress: string | null;
  bankName: string | null;
  bankAccountNumber: string | null;
  iban: string | null;
  bankRouting: string | null;
  swift: string | null;
  /** Seeded server-side (payment confirmations go to the account email); read-only in the UI. */
  paymentEmail: string | null;
  bankingProof: IBankingProof | null;
  completed: boolean;
  completedAt: string | null;
  missingFields: string[];
}

export const useArtistProfiles = (skip = false) => {
  const { data, error, mutate } = useRequest<IArtistProfile[]>(
    skip ? null : '/artist-profile',
    () => api.artistProfile.list()
  );

  /** Replace one profile in the cached list with the object a mutation returned. */
  const updateProfileInCache = useCallback(
    (updated: IArtistProfile) =>
      mutate((profiles) => (profiles || []).map((p) => (p.id === updated.id ? updated : p)), false),
    [mutate]
  );

  return {
    profiles: data,
    anyCompleted: !!data?.some((profile) => profile.completed),
    isLoading: !skip && !data && !error,
    error,
    mutate,
    updateProfileInCache,
  };
};
